import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { promptsListQueryOptions, promptsKeys } from "@/lib/queries";
import type { PromptsListFilters, PromptDto } from "@/lib/queries";
import { filterPromptsBySearch } from "@/features/prompts/lib/prompt-list";
import { PromptsKpiStrip } from "./PromptsKpiStrip";
import { PromptsTable } from "./PromptsTable";
import { PromptsTableToolbar } from "./PromptsTableToolbar";
import { PromptEditorSheet } from "./PromptEditorSheet";
import { DuplicatePromptDialog } from "./DuplicatePromptDialog";
import { DeletePromptDialog } from "./DeletePromptDialog";

export function PromptsPage() {
  const [filters, setFilters] = useState<PromptsListFilters>({});
  const [search, setSearch] = useState("");
  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [duplicatePrompt, setDuplicatePrompt] = useState<PromptDto | null>(null);
  const [deletePrompt, setDeletePrompt] = useState<PromptDto | null>(null);
  const queryClient = useQueryClient();

  const { data: prompts = [], isLoading } = useQuery(promptsListQueryOptions(filters));

  const visiblePrompts = useMemo(
    () => filterPromptsBySearch(prompts, search),
    [prompts, search],
  );

  const selectedPrompt = prompts.find((p) => p.id === selectedPromptId) ?? null;

  const handleRowClick = (prompt: PromptDto) => {
    setSelectedPromptId(prompt.id);
  };

  const handleClose = () => {
    setSelectedPromptId(null);
    setCreateOpen(false);
  };

  const handleSaved = () => {
    void queryClient.invalidateQueries({ queryKey: promptsKeys.all });
  };

  return (
    <div className="flex h-full flex-col gap-4 pt-4">
      <PromptsTableToolbar
        filters={filters}
        onFiltersChange={setFilters}
        search={search}
        onSearchChange={setSearch}
        visibleCount={visiblePrompts.length}
        totalCount={prompts.length}
        onCreateNew={() => setCreateOpen(true)}
      />

      <PromptsKpiStrip prompts={visiblePrompts} isLoading={isLoading} />

      <div className="min-h-0 flex-1 px-4 pb-4">
        <PromptsTable
          prompts={visiblePrompts}
          isLoading={isLoading}
          searchQuery={search}
          onRowClick={handleRowClick}
          onDuplicate={setDuplicatePrompt}
          onDelete={setDeletePrompt}
        />
      </div>

      <PromptEditorSheet
        open={!!selectedPromptId || createOpen}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
        promptId={selectedPromptId}
        prompt={selectedPrompt}
        mode={createOpen ? "create" : "edit"}
        onSaved={handleSaved}
        onDuplicate={setDuplicatePrompt}
        onDelete={setDeletePrompt}
      />

      {duplicatePrompt && (
        <DuplicatePromptDialog
          key={duplicatePrompt.id}
          prompt={duplicatePrompt}
          open
          onOpenChange={(open) => {
            if (!open) setDuplicatePrompt(null);
          }}
        />
      )}

      {deletePrompt && (
        <DeletePromptDialog
          key={deletePrompt.id}
          prompt={deletePrompt}
          open
          onOpenChange={(open) => {
            if (!open) setDeletePrompt(null);
          }}
          onDeleted={() => {
            if (selectedPromptId === deletePrompt.id) {
              setSelectedPromptId(null);
            }
          }}
        />
      )}
    </div>
  );
}
